/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Mutable lock preventing a duplicate next-page request while one is already
 * in flight for the current sentinel intersection (the fetch's own `idle`
 * flag flips asynchronously, so a synchronous re-entrant observer callback
 * could otherwise fire twice for the same intersection).
 */
export type InfiniteScrollGate = {
  locked: boolean;
};

/**
 * Creates a fresh infinite-scroll gate.
 *
 * @returns An unlocked gate.
 */
export function createInfiniteScrollGate(): InfiniteScrollGate {
  return { locked: false };
}

/**
 * Clears the lock so pagination can resume (e.g. after the sentinel hides,
 * or filters reset the list back to page 1).
 *
 * @param gate - Gate to reset.
 */
export function resetInfiniteScrollGate(gate: InfiniteScrollGate): void {
  gate.locked = false;
}

/**
 * Decides whether the list may request the next page. There is no cap on
 * consecutive auto-loads: as long as the sentinel is visible, no fetch is in
 * flight, and more pages remain, scrolling keeps paginating until the whole
 * result set has been loaded.
 *
 * The lock only guards the single synchronous instant right after a request
 * is triggered: recreating the IntersectionObserver on the next render can
 * still close over the pre-fetch `idle` value before the "loading" state
 * update has propagated, which would otherwise re-trigger immediately. Once
 * a callback actually observes `idle === false`, the fetch's own in-flight
 * state is confirmed live, so the lock is released — the `idle` check then
 * blocks further requests on its own until that fetch completes, at which
 * point pagination resumes normally.
 *
 * @param isIntersecting - Whether the sentinel is in the observer root.
 * @param idle - Whether no list fetch is in flight.
 * @param hasMore - Whether `page < totalPages`.
 * @param gate - Session lock shared across observer callbacks.
 * @returns True when the next page should be requested.
 */
export function shouldRequestNextPage(
  isIntersecting: boolean,
  idle: boolean,
  hasMore: boolean,
  gate: InfiniteScrollGate,
): boolean {
  if (!isIntersecting) {
    resetInfiniteScrollGate(gate);
    return false;
  }
  if (!idle) {
    resetInfiniteScrollGate(gate);
    return false;
  }
  if (!hasMore || gate.locked) return false;
  return true;
}

/**
 * Records that a next-page request was triggered, locking out re-entrant
 * requests until the gate is reset.
 *
 * @param gate - Gate to update.
 */
export function markNextPageRequested(gate: InfiniteScrollGate): void {
  gate.locked = true;
}

/**
 * Resolves total pages, stopping when the payload is shorter than page size.
 *
 * @param page - Current 1-based page.
 * @param pageSize - Requested page size.
 * @param itemCount - Items returned for this page.
 * @param reportedPages - Backend `metadata.pages`.
 * @returns Page count the UI should honor.
 */
export function resolvePagedTotal(
  page: number,
  pageSize: number,
  itemCount: number,
  reportedPages: number,
): number {
  if (itemCount < pageSize) return page;
  return reportedPages;
}
